import React, { useState } from 'react';
import { Upload, FileSpreadsheet, Check, X, AlertCircle, Download, CheckCircle } from 'lucide-react';
import * as XLSX from 'xlsx';
import ApiService from '../services/ApiService';

const ClientApprovalImport = ({ onClose, onImportComplete }) => {
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [importResults, setImportResults] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [step, setStep] = useState(1); // 1: Upload, 2: Preview, 3: Results

  const handleFileUpload = async (event) => {
    const uploadedFile = event.target.files[0];
    if (!uploadedFile) return;

    console.log('📁 File selected:', uploadedFile.name);
    setFile(uploadedFile);
    setIsProcessing(true);

    try {
      console.log('📖 Reading Excel file...');
      const data = await readExcelFile(uploadedFile);
      console.log('📊 Raw Excel data:', data);
      
      console.log('🔄 Parsing client approval data...');
      const approvals = parseClientApprovalData(data);
      console.log('✅ Parsed approvals:', approvals);
      
      setParsedData(approvals);
      setStep(2);
    } catch (error) {
      console.error('❌ Error parsing file:', error);
      alert('Error parsing file: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const readExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = e.target.result;
          const workbook = XLSX.read(data, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          resolve(jsonData);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsBinaryString(file);
    });
  };

  const parseClientApprovalData = (data) => {
    if (data.length < 2) {
      throw new Error('File must contain at least a header row and one data row');
    }

    // Find the actual header row (skip title rows)
    let headerRowIndex = 0;
    for (let i = 0; i < Math.min(10, data.length); i++) {
      const row = data[i];
      if (!row || row.length === 0) continue;
      
      // Filter out empty cells and join
      const nonEmptyCells = row.filter(cell => cell !== undefined && cell !== null && cell.toString().trim() !== '');
      if (nonEmptyCells.length < 3) continue; // Need at least 3 header columns
      
      const rowText = nonEmptyCells.join(' ').toLowerCase();
      
      // Look for common header indicators with multiple required fields
      const hasWoNum = rowText.includes('wo num') || rowText.includes('wo#');
      const hasAddress = rowText.includes('address');
      const hasCity = rowText.includes('city');
      const hasWorkOrder = rowText.includes('work order');
      const hasFinalized = rowText.includes('finalized') || rowText.includes('invoiced');
      
      // Must have work order identifier AND location fields OR date fields
      if ((hasWoNum || hasWorkOrder) && (hasAddress || hasCity || hasFinalized)) {
        headerRowIndex = i;
        console.log(`📍 Found headers at row ${i} with ${nonEmptyCells.length} columns`);
        break;
      }
    }

    const headers = data[headerRowIndex];
    const approvals = [];

    console.log('📋 Headers found:', headers);
    console.log('📋 Header at column 0:', headers[0]);
    console.log('📋 Header at column 2:', headers[2]);

    // Expected columns - exact matches from invoice report
    const columnMapping = {
      'workOrderNumber': [
        'invoice #',           // "Invoice #" (invoice reports)
        'work order #',        // "Work Order #" (approval reports)
        'work order number',
        'invoice number',
        'wo num'              // "WO Num" (fallback)
      ],
      'statusDate': [
        'finalized',          // Exact: "Finalized" (column 11)
        'invoiced',
        'completed',
        'ordered',
        'status date'
      ],
      'status': ['work order type', 'status'],
      'address': ['address'],
      'city': ['city'],
      'state': ['state', 'st'],  // "State" (approval reports) or "St" (invoice reports)
      'zip': ['zip'],         // Exact: "Zip"
      'payablePrice': [
        'final amt',          // Exact: "Final Amt" (column 23)
        'vendor amt',
        'payable price',
        'amount'
      ],
      'county': ['county'],
      'required': ['required']
    };

    // Status code mapping for non-MCS work orders
    const statusCodeMapping = {
      'approved': 'Approved',
      'pending': 'Pending',
      'cancelled': 'Cancelled',
      'rejected': 'Rejected'
    };

    // Find column indices - respecting pattern priority order
    const columnIndices = {};
    Object.keys(columnMapping).forEach(field => {
      let foundIndex = -1;
      
      // Try each pattern in priority order (first pattern = highest priority)
      for (const pattern of columnMapping[field]) {
        const patternLower = pattern.toLowerCase();
        
        const index = headers.findIndex(header => {
          if (header === undefined || header === null || header === '') return false;
          const headerLower = header.toString().toLowerCase().trim();
          if (!headerLower) return false;
          
          // For short patterns (≤3 chars like "st", "zip"), require exact match
          if (patternLower.length <= 3) {
            return headerLower === patternLower;
          }
          // For longer patterns, allow exact or contains match
          return headerLower === patternLower || headerLower.includes(patternLower);
        });
        
        if (index !== -1) {
          foundIndex = index;
          console.log(`✅ Mapped "${field}" to column ${index}: "${headers[index]}" (matched pattern: "${pattern}")`, 
                      field === 'workOrderNumber' ? `<-- THIS IS THE WORK ORDER COLUMN` : '');
          break; // Stop at first match (highest priority)
        }
      }
      
      if (foundIndex !== -1) {
        columnIndices[field] = foundIndex;
      } else {
        console.log(`⚠️  Column "${field}" not found in headers`);
      }
    });

    // Parse data rows (start after header row)
    for (let i = headerRowIndex + 1; i < data.length; i++) {
      const row = data[i];
      if (!row || row.length === 0) continue;

      // Skip empty rows
      const hasData = row.some(cell => cell !== undefined && cell !== null && cell.toString().trim() !== '');
      if (!hasData) continue;

      // Skip "Total" or summary rows (check first few columns for common total indicators)
      const firstCells = row.slice(0, 5).filter(c => c).join(' ').toLowerCase();
      if (firstCells.includes('total') || 
          firstCells.includes('grand total') || 
          firstCells.includes('subtotal') ||
          firstCells.includes('summary') ||
          firstCells.includes('sum')) {
        console.log(`⏭️ Skipping total/summary row ${i}`);
        continue;
      }
      
      // Skip rows where the work order column appears to be a total/sum
      const woValue = row[columnIndices.workOrderNumber || 0];
      if (woValue && typeof woValue === 'string') {
        const woLower = woValue.toLowerCase();
        if (woLower.includes('total') || woLower.includes('sum') || woLower.includes('count')) {
          console.log(`⏭️ Skipping row ${i}: Work order column contains total/sum indicator`);
          continue;
        }
      }

      try {
        const workOrderNum = getColumnValue(row, columnIndices.workOrderNumber, '');
        
        // Simple logic: MCS work orders (start with M) = Approved
        let mappedStatus = 'Approved';
        if (workOrderNum && workOrderNum.toString().toUpperCase().startsWith('M')) {
          mappedStatus = 'Approved';
        } else {
          // For non-MCS, check if there's an explicit status
          const rawStatus = getColumnValue(row, columnIndices.status, 'Approved');
          mappedStatus = statusCodeMapping[rawStatus.toString().toLowerCase()] || 
                        statusCodeMapping[rawStatus.toString()] || 
                        'Approved';
        }

        console.log(`🔄 Row ${i}: WO# "${workOrderNum}" → Status: "${mappedStatus}"`);

        
        const approval = {
          workOrderNumber: workOrderNum,
          statusDate: parseDate(getColumnValue(row, columnIndices.statusDate, new Date())),
          status: mappedStatus,
          address: getColumnValue(row, columnIndices.address, ''),
          city: getColumnValue(row, columnIndices.city, ''),
          state: getColumnValue(row, columnIndices.state, ''),
          zip: getColumnValue(row, columnIndices.zip, ''),
          payablePrice: parseFloat(getColumnValue(row, columnIndices.payablePrice, 0)) || 0,
          county: getColumnValue(row, columnIndices.county, ''),
          required: getColumnValue(row, columnIndices.required, '')
        };
        
        // Log first few work orders to verify we're getting the right column
        if (i <= headerRowIndex + 3) {
          console.log(`📝 Row ${i} Work Order: "${workOrderNum}" (from column ${columnIndices.workOrderNumber})`);
        }

        // Only include if we have essential data
        if (approval.workOrderNumber && approval.status) {
          approvals.push(approval);
          if (approvals.length <= 3) {
            console.log(`✅ Added approval ${approvals.length}:`, approval.workOrderNumber, approval.status);
          }
        } else {
          console.log(`⏭️ Skipping row ${i}: Missing required fields - WO: "${approval.workOrderNumber}", Status: "${approval.status}"`);
        }
      } catch (error) {
        console.error(`❌ Error parsing row ${i}:`, error);
        console.error(`   Row data:`, row);
      }
    }

    console.log(`\n📊 Parsing Summary:`);
    console.log(`  Total rows processed: ${data.length - headerRowIndex - 1}`);
    console.log(`  Valid approvals found: ${approvals.length}`);
    console.log(`  Header row index: ${headerRowIndex}`);
    console.log(`  Column mappings:`, columnIndices);
    
    if (approvals.length === 0) {
      console.error('❌ No valid approvals found!');
      console.error('Debug info:');
      console.error('  - Check that Work Order Number column was found:', columnIndices.workOrderNumber);
      console.error('  - Check that Status column was found:', columnIndices.status);
      console.error('  - First data row (after header):', data[headerRowIndex + 1]);
      throw new Error(`No valid client approvals found in the file. Header row: ${headerRowIndex}, Data rows: ${data.length - headerRowIndex - 1}`);
    }

    return approvals;
  };

  const getColumnValue = (row, columnIndex, defaultValue = '') => {
    if (columnIndex === undefined || columnIndex === -1 || !row[columnIndex]) {
      return defaultValue;
    }
    return row[columnIndex].toString().trim();
  };

  const parseDate = (dateString) => {
    if (!dateString) return new Date().toISOString().split('T')[0];
    
    try {
      // Handle Excel date numbers
      if (!isNaN(dateString) && dateString > 25569) {
        const date = new Date((dateString - 25569) * 86400 * 1000);
        return date.toISOString().split('T')[0];
      }
      
      // Handle string dates
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toISOString().split('T')[0];
      }
    } catch (error) {
      console.warn('Error parsing date:', dateString);
    }
    
    return new Date().toISOString().split('T')[0];
  };

  const handleImport = async () => {
    console.log('📤 Starting client approval import with data:', parsedData);
    console.log('📊 Sample approval data:', parsedData.slice(0, 3));
    setIsProcessing(true);
    
    try {
      console.log('📡 Calling API to import client approvals...');
      const result = await ApiService.importClientApprovals(parsedData);
      console.log('✅ Import API response:', result);
      
      // Enhanced logging for debugging
      console.log('📋 Import Results Summary:');
      console.log(`• Success Count: ${result.successCount}`);
      console.log(`• Error Count: ${result.errorCount}`);
      console.log(`• Import Batch: ${result.importBatch}`);
      
      if (result.errors && result.errors.length > 0) {
        console.log('❌ Import Errors:', result.errors);
      }
      
      setImportResults(result);
      setStep(3);
      
      if (onImportComplete) {
        console.log('🔄 Calling onImportComplete callback');
        onImportComplete(result);
      }
    } catch (error) {
      console.error('Import error:', error);
      alert('Import failed: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const downloadTemplate = () => {
    // Basic approval format template
    const basicTemplate = [
      ['Work Order Number', 'Status Date', 'Status', 'Address', 'City', 'State', 'Zip', 'Payable Price', 'County', 'Required'],
      ['T0034918631', '9/3/2025', 'Approved', '1 Canal St', 'Scotland', 'IN', '47457', '0', '', ''],
      ['M15237233', '8/28/2025', 'Approved', '843 Elwood', '', 'IN', '', '632', '', ''],
      ['T0035208763', '8/22/2025', 'Cancelled', '123 S Adams St', 'Princeton', 'IN', '47670', '20', '', '']
    ];

    // Invoice format template (alternative format)
    const invoiceTemplate = [
      ['', '', 'Your Invoice Export Title Here'],
      [''],
      [''],
      [''],
      ['WO Num', '', 'Invoice #', 'Work Order Type', '', 'Cust #', '', 'Loan Num', 'Ordered', 'Completed', 'Invoiced', 'Finalized', 'Auto', 'Address', 'City', '', 'St', 'Zip', 'Lawn', 'Lot', 'Sub', 'Late Fee', 'Vendor Amt', 'Final Amt', 'Check #'],
      ['15264040', '', 'M15264040', 'Initial Secure', '', '548', '', '******7514', '9/17/2025', '9/18/2025', '10/1/2025', '10/7/2025', 'No', '310 NEW ENGLAND CT', 'LOUISVILLE', '', 'KY', '40214', '9324', '10780', '', '0', '200.62', '196.88', ''],
      ['15271814', '', 'M15271814', 'Re-Secure', '', '573', '', '******7112', '9/26/2025', '9/26/2025', '10/10/2025', '10/17/2025', 'No', '2613 HARMONY RD', 'LOUISVILLE', '', 'KY', '40299', '10559', '11761', '', '0', '37.50', '37.5', '']
    ];

    const wb = XLSX.utils.book_new();
    
    // Add both templates as separate sheets
    const ws1 = XLSX.utils.aoa_to_sheet(basicTemplate);
    XLSX.utils.book_append_sheet(wb, ws1, 'Basic Approval Format');
    
    const ws2 = XLSX.utils.aoa_to_sheet(invoiceTemplate);
    XLSX.utils.book_append_sheet(wb, ws2, 'Invoice Report Format');
    
    XLSX.writeFile(wb, 'client-approvals-template.xlsx');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Import Client Approvals</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Progress Steps */}
          <div className="mb-8">
            <div className="flex items-center space-x-4">
              <div className={`flex items-center ${step >= 1 ? 'text-blue-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>1</div>
                <span className="ml-2 text-sm font-medium">Upload Report</span>
              </div>
              <div className={`flex-1 h-1 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-200'} rounded`}></div>
              <div className={`flex items-center ${step >= 2 ? 'text-blue-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>2</div>
                <span className="ml-2 text-sm font-medium">Preview & Process</span>
              </div>
              <div className={`flex-1 h-1 ${step >= 3 ? 'bg-blue-600' : 'bg-gray-200'} rounded`}></div>
              <div className={`flex items-center ${step >= 3 ? 'text-blue-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>3</div>
                <span className="ml-2 text-sm font-medium">Results</span>
              </div>
            </div>
          </div>

          {/* Step 1: Upload */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-blue-800 mb-2">Import Client Approval Reports</h3>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Upload Excel files from your clients showing approved work orders</li>
                  <li>• Supports multiple formats: Approval reports, Finalized Invoice reports</li>
                  <li>• The system will apply your business logic automatically:</li>
                  <li>&nbsp;&nbsp;- Department detection (M prefix = MCS, others = GAM)</li>
                  <li>&nbsp;&nbsp;- MCS gets 25% discount automatically</li>
                  <li>&nbsp;&nbsp;- Full address concatenation</li>
                  <li>&nbsp;&nbsp;- Reference string creation</li>
                  <li>• Invoice reports: Uses "Finalized" date as approval date</li>
                </ul>
              </div>

              <div className="flex justify-center mb-4">
                <button
                  onClick={downloadTemplate}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Download Template
                </button>
              </div>

              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <FileSpreadsheet className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">Choose your client approval report file</p>
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className="cursor-pointer bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
                >
                  <Upload className="w-4 h-4" />
                  Select File
                </label>
              </div>

              {isProcessing && (
                <div className="text-center py-4">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <p className="text-gray-600 mt-2">Processing approval data...</p>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Preview */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="bg-green-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-green-800 mb-2">Client Approval Preview</h3>
                <p className="text-sm text-green-700">
                  Found {parsedData.length} client approvals. Business logic will be applied automatically.
                </p>
              </div>

              <div className="overflow-x-auto max-h-96 border border-gray-200 rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left">Work Order #</th>
                      <th className="px-4 py-2 text-left">Status</th>
                      <th className="px-4 py-2 text-left">Date</th>
                      <th className="px-4 py-2 text-left">Address</th>
                      <th className="px-4 py-2 text-right">Amount</th>
                      <th className="px-4 py-2 text-left">Auto-Detected</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {parsedData.slice(0, 20).map((approval, index) => {
                      const department = approval.workOrderNumber.trim().startsWith('M') ? 'MCS' : 'GAM';
                      
                      // Both MCS and GAM use the approval amount as source of truth
                      // MCS: imported amount is post-discount (75% of original)
                      // GAM: imported amount is the full amount (no discount)
                      let originalAmount, finalAmount, displayNote;
                      
                      if (department === 'MCS') {
                        // Imported amount is post-discount, calculate original
                        finalAmount = approval.payablePrice; // This is what customer pays ($15)
                        originalAmount = finalAmount / 0.75; // This goes in Xero invoice ($20)
                        displayNote = `Xero invoice: $${originalAmount.toFixed(2)} with 25% discount = $${finalAmount.toFixed(2)}`;
                      } else {
                        // GAM: approval amount goes directly to Xero invoice (no discount)
                        originalAmount = approval.payablePrice;
                        finalAmount = approval.payablePrice;
                        displayNote = `Xero invoice: $${finalAmount.toFixed(2)} (no discount)`;
                      }
                      
                      return (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-2 font-medium">{approval.workOrderNumber}</td>
                          <td className="px-4 py-2">
                            <span className={`px-2 py-1 rounded text-xs ${
                              approval.status === 'Approved' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {approval.status}
                            </span>
                          </td>
                          <td className="px-4 py-2">{new Date(approval.statusDate).toLocaleDateString()}</td>
                          <td className="px-4 py-2">{approval.address} {approval.city} {approval.state}</td>
                          <td className="px-4 py-2 text-right">
                            ${approval.payablePrice.toFixed(2)}
                            {displayNote && (
                              <div className="text-xs text-blue-600">
                                {displayNote}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-2">
                            <span className={`px-2 py-1 rounded text-xs ${
                              department === 'MCS' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                            }`}>
                              {department}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {parsedData.length > 20 && (
                <p className="text-sm text-gray-500 text-center">
                  Showing first 20 rows. {parsedData.length - 20} more rows will be imported.
                </p>
              )}

              <div className="flex justify-between">
                <button
                  onClick={() => setStep(1)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Back
                </button>
                <button
                  onClick={handleImport}
                  disabled={isProcessing}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {isProcessing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Importing...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      Import {parsedData.length} Approvals
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Results */}
          {step === 3 && importResults && (
            <div className="space-y-6">
              <div className={`rounded-lg p-4 ${importResults.errorCount > 0 ? 'bg-yellow-50' : 'bg-green-50'}`}>
                <h3 className={`text-lg font-semibold mb-2 ${importResults.errorCount > 0 ? 'text-yellow-800' : 'text-green-800'}`}>
                  Import Complete
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-600">{importResults.successCount}</p>
                    <p className="text-sm text-gray-600">Successfully Imported</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-red-600">{importResults.errorCount}</p>
                    <p className="text-sm text-gray-600">Errors</p>
                  </div>
                </div>
              </div>

              {importResults.errorCount > 0 && (
                <div className="bg-red-50 rounded-lg p-4">
                  <h4 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Errors Encountered
                  </h4>
                  <ul className="text-sm text-red-700 space-y-1 max-h-32 overflow-y-auto">
                    {importResults.errors.map((error, index) => (
                      <li key={index}>• {error}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  What Happens Next
                </h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Client approvals have been imported and processed</li>
                  <li>• Business logic applied (department detection, discounts, addresses)</li>
                  <li>• Go to "Xero Staging" tab to see work orders ready for processing</li>
                  <li>• Match these approvals with your completed work orders</li>
                  <li>• Batch process invoices and bills to Xero</li>
                </ul>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={onClose}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientApprovalImport;
